import { FaWifi, FaShieldAlt, FaBriefcase, FaHandshake } from 'react-icons/fa';

const WhyFigo = () => {
  const differentiators = [
    {
      icon: <FaWifi />,
      title: 'Offline-First Systems',
      description: 'Our solutions work even when the internet doesn\'t. Built for the realities of African infrastructure.',
      highlight: 'Works without internet',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Server infrastructure and network connectivity'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Nigerian Regulatory Awareness',
      description: 'We understand NDPR, CBN guidelines, and local compliance requirements. Your systems are audit-ready from day one.',
      highlight: 'NDPR & CBN compliant',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Professional compliance and regulatory documentation'
    },
    {
      icon: <FaBriefcase />,
      title: 'Business-First, Not Tech-First',
      description: 'We solve business problems, not show off technology. Every solution is measured by its impact on your bottom line.',
      highlight: 'ROI-focused',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Business analytics and growth metrics dashboard'
    },
    {
      icon: <FaHandshake />,
      title: 'Long-Term Partnership Model',
      description: 'We\'re not vendors who disappear after launch. We grow with you, supporting your success for years to come.',
      highlight: 'Ongoing support',
      image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Team collaboration and partnership'
    }
  ];

  return (
    <section style={{
      padding: '6rem 5%',
      background: 'var(--bg-primary)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <span style={{
            display: 'inline-block',
            padding: '0.5rem 1.25rem',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(13, 148, 136, 0.15) 100%)',
            border: '1px solid rgba(37, 99, 235, 0.25)',
            borderRadius: '50px',
            color: 'var(--figo-primary)',
            fontSize: '0.85rem',
            fontWeight: '600',
            marginBottom: '1.25rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Why Choose Us
          </span>

          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 2.75rem)',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '1rem',
            lineHeight: '1.2'
          }}>
            Why FIGO Is Different
          </h2>

          <p style={{
            fontSize: '1.1rem',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            We're not just another tech company. We understand the unique challenges of doing business in Nigeria.
          </p>
        </div>

        {/* Alternating Image-Text Rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4rem' }}>
          {differentiators.map((item, index) => {
            const isReversed = index % 2 !== 0;

            return (
              <div
                key={index}
                className="why-figo-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '3rem',
                  alignItems: 'center',
                  direction: isReversed ? 'rtl' : 'ltr'
                }}
              >
                {/* Image Side */}
                <div style={{
                  direction: 'ltr',
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  aspectRatio: '4 / 3',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)'
                }}>
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 0.5s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  />
                  {/* Gradient overlay */}
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(13, 148, 136, 0.1) 100%)',
                    pointerEvents: 'none'
                  }} />
                </div>

                {/* Text Side */}
                <div style={{
                  direction: 'ltr',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  padding: '1rem 0'
                }}>
                  {/* Highlight Badge */}
                  <span style={{
                    display: 'inline-block',
                    width: 'fit-content',
                    padding: '0.4rem 1rem',
                    background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(13, 148, 136, 0.15) 100%)',
                    border: '1px solid rgba(37, 99, 235, 0.2)',
                    borderRadius: '50px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'var(--figo-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '1.25rem'
                  }}>
                    {item.highlight}
                  </span>

                  {/* Icon + Title */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #2563eb 0%, #0d9488 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.25rem',
                      color: 'white',
                      flexShrink: 0,
                      boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)'
                    }}>
                      {item.icon}
                    </div>
                    <h3 style={{
                      fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      lineHeight: '1.3',
                      margin: 0
                    }}>
                      {item.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p style={{
                    fontSize: '1.05rem',
                    color: 'var(--text-secondary)',
                    lineHeight: '1.8',
                    margin: 0,
                    maxWidth: '480px'
                  }}>
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          .why-figo-row {
            grid-template-columns: 1fr !important;
            direction: ltr !important;
            gap: 1.5rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default WhyFigo;
